import { BuildCircle, House } from '@mui/icons-material';
import { Box, Card, CardContent, Grid, List, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Fragment, cloneElement, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Link, Loader, Page, PageTitle } from '../../components';
import { ExternalLink } from '../../components/link/ExternalLink';
import { UserContext } from '../../context';
import {
  downloadContractDocument,
  downloadFileVersion,
  getFinalPriceContract,
  getNews
} from '../../fetch';
import {
  HOMEOWNERS_PORTAL_SLUG,
  HOMEOWNERS_PORTAL_THRIVE_SLUG,
  alphaSort,
  downloadFile,
  getDownloadFileName,
  hasNavAccess,
  hasNavActiveFlags,
  isAllowedToSeeHomeownerPortal,
  isExternalUser,
  isHomeowner,
  isLenderOrLenderProcessor,
  isThriveHomeowner,
  mainNavLinks,
  filterNavByAllowedRoutes
} from '../../helpers';
import { useFlags } from '../../hooks';
import { FeatureFlag, INavLink, INews, UserRoles } from '../../models';
import { NewsCard } from '../news/news-card';
import { NoRoles } from '../no-roles';
import { HomeownerCeremonies } from './homeowner-ceremonies';
import { useAllowedNavRoutes } from '../../hooks/useAllowedNavRoutes';
const { LegacySystem } = require('../../buildSettings.json');

const HomeCard = ({ link }: { link: INavLink }) => {
  return (
    <Card>
      <CardContent sx={{ textAlign: 'center', padding: '24px' }}>
        {cloneElement(link.icon, { fontSize: 'large', color: 'primary' })}
        <Typography>{link.name}</Typography>
      </CardContent>
    </Card>
  );
};

export const Home = () => {
  const userContext = useContext(UserContext);
  const { user } = userContext;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const buildDocumentId = queryParams.get('builddocumentid');
  const token = queryParams.get('token');
  const buildDocumentVersionId = queryParams.get('builddocumentversionid');
  const finalPriceContractPages = queryParams.get('final-price-contract-pages');
  const buildId = queryParams.get('buildId');
  const featureFlags = useFlags();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(true);
  const [news, setNews] = useState<INews[]>([]);
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);

  const filteredLinks = useMemo(() => {
    let links = mainNavLinks;
    if (featureFlags?.authorizeUserNavigationAccess && availableLinks?.size > 0) {
      links = filterNavByAllowedRoutes(links, new Set(availableLinks));
    } else {
      links = links.filter(link => hasNavAccess(link?.roles, userContext));
    }

    // Filter out "Homeowner Portals" for homeowners (BLClient users) because it is manually added in the home page
    if (isHomeowner(user)) {
      links = links.filter(link => link.name !== 'Homeowner Portals');
    }

    return links;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainNavLinks, availableLinks, featureFlags, user]);

  const handleDownloadDocument = async (buildDocumentId: number, fileName: string) => {
    try {
      let res: any;
      // if buildDocumentVersionId is not provided, download the document
      if (!buildDocumentVersionId) {
        res = await downloadContractDocument(buildDocumentId, null, token);
      } else {
        res = await downloadFileVersion(buildDocumentId, buildDocumentVersionId, token);
      }

      downloadFile(res?.data, getDownloadFileName(res) || fileName);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
        variant: 'error'
      });
    }
  };

  const fetchFinalPriceContract = async () => {
    try {
      const res = await getFinalPriceContract(
        buildId,
        finalPriceContractPages,
        buildDocumentVersionId
      );
      downloadFile(res.data, getDownloadFileName(res) ?? 'Signed-Final-Contract.pdf');
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading Final Price Contract, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    }
  };

  const fetchNews = async () => {
    try {
      const newsData = await getNews({
        onlyActive: true,
        sortBy: 'CreatedDate',
        sortDirection: 'Desc'
      });
      setNews(newsData.records);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    if (buildDocumentId) {
      handleDownloadDocument(Number(buildDocumentId), 'Final-Contract.pdf');
    }
    if (buildId) {
      fetchFinalPriceContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const showNews = news?.length > 0;

  if (user?.userRoles?.length === 0) {
    return <NoRoles />;
  }

  return (
    <Page
      title="Home"
      showSideBar={false}
      showSideNav={!isHomeowner(user)}
      disableDesktopCollapse={isHomeowner(user)}
    >
      <PageTitle title="Home" />
      {isLoading && <Loader type="inline" position="centered" />}
      {!isLoading && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={showNews ? 8 : 12} xl={showNews ? 9 : 12}>
            <Grid spacing={2} container alignItems="center">
              {alphaSort(
                [
                  ...filteredLinks,
                  // these nav items only shows on the homepage and not in the side nav which is why it is appended here
                  {
                    id: 'homeowner-portal',
                    name: 'Homeowner Portal',
                    icon: <House fontSize="medium" />,
                    url: featureFlags.homeownerPortalV2
                      ? '/homeowner-portal'
                      : `/custom-pages/${
                          isThriveHomeowner(user)
                            ? HOMEOWNERS_PORTAL_THRIVE_SLUG
                            : HOMEOWNERS_PORTAL_SLUG
                        }`,
                    roles: [UserRoles.BLClient]
                  },
                  {
                    name: 'My Build',
                    icon: <BuildCircle fontSize="medium" />,
                    legacyUrl: `${LegacySystem}/Portals/Sales/BuildOptions.aspx`,
                    url: '/my-build',
                    featureFlag: FeatureFlag.clientViewBuild,
                    roles: [UserRoles.BLClient]
                  }
                ],
                'name'
              )
                // check to make sure the the FF are active
                .filter(item => !!hasNavActiveFlags(item, featureFlags))
                // filter out the reporting link for Lenders and Lender Processors
                .filter(link => {
                  if (isLenderOrLenderProcessor(userContext?.user)) {
                    return link.name !== 'Reporting';
                  }
                  return true;
                })
                // one off check here to be explict that only a BL_Client can see the "My Build" home link
                .map(link => {
                  if (
                    link.name === 'My Build' &&
                    !userContext?.user?.userRoles?.includes(UserRoles.BLClient)
                  ) {
                    return null;
                  }

                  if (
                    link.name === 'Homeowner Portals' &&
                    (!isAllowedToSeeHomeownerPortal(user) || featureFlags.homeownerPortalV2)
                  ) {
                    return null;
                  }

                  if (
                    link.id === 'homeowner-portal' &&
                    !userContext?.user?.userRoles?.includes(UserRoles.BLClient)
                  ) {
                    return null;
                  }

                  // if the user is an external user, remove the How-To's link
                  if (link.name === "How-To's" && isExternalUser(userContext?.user)) {
                    return null;
                  }

                  if (link.id === 'reporting' && !user?.isMember) {
                    return null;
                  }

                  return link;
                })
                .map(link => {
                  if (
                    link?.name === 'Documentation' &&
                    user?.userRoles?.length === 1 &&
                    user?.userRoles?.includes(UserRoles.BLClosing)
                  ) {
                    return {
                      ...link,
                      name: 'Closing'
                    };
                  }
                  return link;
                })
                // filter out null values
                .filter(Boolean)
                .map((link: INavLink, i) => {
                  const hasLegacyLink = link.legacyUrl && !link.url ? true : false;
                  const hasLegacyLinkWithFFOff =
                    link.featureFlag && link.legacyUrl && !featureFlags[link.featureFlag]
                      ? true
                      : false;

                  return (
                    <Fragment key={`${i}`}>
                      <Grid xs={12} sm={6} md={4} item>
                        {hasLegacyLink || hasLegacyLinkWithFFOff ? (
                          <ExternalLink to={link.legacyUrl}>
                            <HomeCard link={link} />
                          </ExternalLink>
                        ) : (
                          <Link to={link.url}>
                            <HomeCard link={link} />
                          </Link>
                        )}
                      </Grid>
                    </Fragment>
                  );
                })}
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3} rowGap={1} flexDirection="column" display="flex">
            {showNews && (
              <Box padding="1rem" border="1px solid #e1e1e1" borderRadius="4px">
                <List
                  aria-labelledby="list-header"
                  subheader={
                    <Typography
                      color="primary"
                      variant="h5"
                      id="list-header"
                      sx={{ marginBottom: theme => theme.spacing(0.5) }}
                    >
                      News
                    </Typography>
                  }
                >
                  {news.slice(0, 3).map((newsItem: INews, index) => {
                    return (
                      <Box mb={1} key={`${index}`}>
                        <NewsCard newsItem={newsItem} />
                      </Box>
                    );
                  })}
                  {news?.length > 3 && <Link to="/news">All News</Link>}
                </List>
              </Box>
            )}
            {featureFlags.homeownerClosingCeremonies && isHomeowner(user) && (
              <HomeownerCeremonies />
            )}
          </Grid>
        </Grid>
      )}
    </Page>
  );
};
